import { Icon, Input, InputProps, useIntl } from "@metsooutotec/modes-react-components"
import { FC, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { AssetSection, EditingAssetSectionWindow } from "../../models/asset-section-models"
import { handleUploadFileClick, resetRecordFormData } from "components/utils/modal-helper"
import { validateFile, validateText } from "validators/inputValidators"

interface AssetSectionDetailsProps {
    assetSection: AssetSection
    editAssetSection: EditingAssetSectionWindow
    setEditAssetSection: React.Dispatch<React.SetStateAction<EditingAssetSectionWindow>>
    errorMessage: string
}

interface AssetSectionFormField {
    id: string
    label: string
    placeholder: string
    type: string
    validate?: (value: string) => string
    onFileUpload?: () => void
    unSelectFile?: () => void
}

const AssetSectionEditorDetails: FC<AssetSectionDetailsProps> = observer(({ assetSection, editAssetSection, setEditAssetSection, errorMessage }) => {
    const { formatMessage } = useIntl()
    const [isError, setIsError] = useState<{ isError: boolean; errorMessage: string }>({ isError: false, errorMessage: "" })
    const [selectedPdf, setSelectedPdf] = useState<File | null>(null)
    const [selectedSvg, setSelectedSvg] = useState<File | null>(null)

    const [formData, setFormData] = useState<Record<string, string>>({
        assetSectionName: assetSection.name || "",
        assetSectionDescription: assetSection.description || "",
        uploadPdfName: selectedPdf?.name as string,
        uploadSvgName: selectedSvg?.name as string,
    })
    const [formErrors, setFormErrors] = useState<Record<string, string>>({
        assetSectionName: "",
        assetSectionDescription: "",
        uploadPdfName: "",
        uploadSvgName: "",
    })

    useEffect(() => {
        resetRecordFormData(setFormData)
        setFormData({
            assetSectionName: assetSection.name || "",
            assetSectionDescription: assetSection.description || "",
            uploadPdfName: selectedPdf?.name as string,
            uploadSvgName: selectedSvg?.name as string,
        })
    }, [])

    useEffect(() => {
        setEditAssetSection({
            ...editAssetSection,
            assetSectionPdf: selectedPdf,
            assetSectionSvg: selectedSvg,
        })
    }, [selectedPdf, selectedSvg])

    useEffect(() => {
        if (editAssetSection.assetSectionPdf === null) {
            setSelectedPdf(null)
            setFormData(prevData => ({
                ...prevData,
                uploadPdfName: "",
            }))
        }
        if (editAssetSection.assetSectionSvg === null) {
            setSelectedSvg(null)
            setFormData(prevData => ({
                ...prevData,
                uploadSvgName: "",
            }))
        }
    }, [editAssetSection.assetSectionPdf, editAssetSection.assetSectionSvg])

    const handleChange = (fieldName: string, value: string) => {
        isError.isError = false
        const updatedData = {
            ...formData,
            [fieldName]: value,
        }
        const { assetSectionName, assetSectionDescription } = updatedData
        setEditAssetSection({
            ...editAssetSection,
            name: assetSectionName,
            description: assetSectionDescription,
        })
        setFormData(updatedData)
    }
    const handleValidation = (fieldName: string, validationError: string | any): void => {
        setFormErrors(prevErrors => ({
            ...prevErrors,
            [fieldName]: validationError,
        }))
    }

    const onClearClick = (inputId: string) => {
        const updatedData = {
            ...formData,
            [inputId]: "",
        }
        setFormErrors(prevErrors => ({
            ...prevErrors,
            [inputId]: "",
        }))
        const { assetSectionName, assetSectionDescription } = updatedData
        setFormData(updatedData)
        setEditAssetSection({
            ...editAssetSection,
            name: assetSectionName,
            description: assetSectionDescription,
        })
    }
    const handlePdfClick = () => {
        handleUploadFileClick(".pdf", setSelectedPdf, setFormData, setFormErrors, "uploadPdfName")
    }
    const handleSvgClick = () => {
        handleUploadFileClick(".svg", setSelectedSvg, setFormData, setFormErrors, "uploadSvgName")
    }
    const clearPdf = () => {
        setFormData(prevData => ({
            ...prevData,
            uploadPdfName: "",
        }))
        setSelectedPdf(null)
    }
    const clearSvg = () => {
        setFormData(prevData => ({
            ...prevData,
            uploadSvgName: "",
        }))
        setSelectedSvg(null)
    }

    const labels = {
        assetSectionName: formatMessage("assetSectionModal.name"),
        assetSectionDescription: formatMessage("assetSectionModal.description"),
        confirm: formatMessage("modal.confirm"),
        cancel: formatMessage("modal.cancel"),
        header: formatMessage("assetSectionModal.header"),
        description: formatMessage("modal.description"),
        uploadPdf: formatMessage("assetSectionModal.uploadPdf"),
        uploadSvg: formatMessage("assetSectionModal.uploadSvg"),
    }
    const inputConfig: AssetSectionFormField[] = [
        {
            id: "assetSectionName",
            label: labels.assetSectionName,
            placeholder: `Type ${labels.assetSectionName} here...`,
            type: "string",
            validate: validateText,
        },
        {
            id: "assetSectionDescription",
            label: labels.assetSectionDescription,
            placeholder: `Type ${labels.assetSectionDescription} here...`,
            type: "string",
            validate: validateText,
        },
        {
            id: "uploadPdfName",
            label: labels.uploadPdf,
            placeholder: `select from files...`,
            type: "string",
            onFileUpload: handlePdfClick,
            unSelectFile: clearPdf,
            validate: validateFile,
        },
        {
            id: "uploadSvgName",
            label: labels.uploadSvg,
            placeholder: `select from files...`,
            type: "string",
            onFileUpload: handleSvgClick,
            unSelectFile: clearSvg,
            validate: validateFile,
        },
    ]
    const renderInputs = (): React.ReactNode => {
        return (
            <>
                {errorMessage && <p style={{ color: "red" }}> {errorMessage}</p>}
                {/* First set of fields */}
                {inputConfig.slice(0, 2).map((input: AssetSectionFormField, index) => {
                    const { id, label, validate, type, placeholder } = input
                    const clearIcon = formData[id] && <Icon className='icon-cross-circle-fill-16' onClick={() => onClearClick(id)} />
                    const inputProps: InputProps = {
                        id,
                        label,
                        placeholder,
                        validate,
                        onValidation: (validationError: string | undefined) => handleValidation(id, validationError),
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange(id, e.target.value),
                        type,
                        error: formErrors[id],
                        spanning: true,
                        heightClass: "small",
                        required: true,
                        uiRequired: true,
                        value: formData[id],
                        icon: clearIcon,
                    }

                    return <Input key={id} style={{ marginBottom: "var(--size-xs)" }} {...inputProps} />
                })}
                {/* Second set of fields */}
                {inputConfig.slice(2).map((input: AssetSectionFormField, index) => {
                    const { id, label, type, placeholder, onFileUpload, unSelectFile, validate } = input
                    const icons = (
                        <>
                            {formData[id] && <Icon className='icon-cross-circle-fill-16' onClick={unSelectFile} style={{ marginRight: "2px" }} />}
                            <Icon className='icon-upload-16' onClick={onFileUpload} />
                        </>
                    )
                    const inputProps: InputProps = {
                        id,
                        label,
                        placeholder,
                        validate,
                        onValidation: (validationError: string | undefined) => handleValidation(id, validationError),
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange(id, e.target.value),
                        type,
                        spanning: true,
                        heightClass: "small",
                        error: formErrors[id],
                        readOnly: true,
                        onClick: onFileUpload,
                        required: false,
                        uiRequired: false,
                        value: formData[id],
                        icon: icons,
                    }
                    return <Input key={id} style={{ marginBottom: "var(--size-xs)" }} {...inputProps} />
                })}
            </>
        )
    }
    return (
        <>
            <p style={{ fontSize: "1.5vw", marginBottom: "0.25em" }}>
                <strong>Asset Section Details</strong>
            </p>
            {renderInputs()}
            <div style={{ marginBottom: "1vw" }} />
        </>
    )
})

export default AssetSectionEditorDetails
